import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney } from '../api';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';

interface OpenItem {
  id: string;
  number: string;
  owner_name: string;
  period_end: string;
  due_date: string | null;
  gross_cents: number;
  paid_cents: number;
  open_cents: number;
  days_overdue: number;
  dunning_level: number;
  last_dunning_at: string | null;
}

interface Summary {
  openCents: number;
  overdueCents: number;
  invoices: number;
  buckets: { label: string; cents: number; count: number }[];
}

interface Payment {
  id: string;
  invoice_number: string;
  owner_name: string;
  amount_cents: number;
  paid_on: string;
  method: string;
  reference: string | null;
  created_by_name: string | null;
}

const METHODS = ['ueberweisung', 'lastschrift', 'bar', 'verrechnung', 'sonstiges'] as const;
const METHOD_LABEL: Record<string, string> = {
  ueberweisung: 'Überweisung',
  lastschrift: 'Lastschrift',
  bar: 'bar',
  verrechnung: 'Verrechnung',
  sonstiges: 'sonstiges',
};

export function ReceivablesPage() {
  const [items, setItems] = useState<OpenItem[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  // Zahlungen über dem Rechnungsbetrag. Sie stehen in keiner Liste der
  // offenen Posten - ohne eigene Anzeige würde das Geld schlicht verschwinden.
  const [credits, setCredits] = useState<OpenItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [dunning, setDunning] = useState<(OpenItem & { next_level: number })[]>([]);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [payFor, setPayFor] = useState<OpenItem | null>(null);
  const [payment, setPayment] = useState({ amount: '', paidOn: '', method: 'ueberweisung', reference: '' });

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [receivables, paymentList, dunningList] = await Promise.all([
        api<{ items: OpenItem[]; summary: Summary; credits: OpenItem[] }>('/api/billing/receivables', {
          query: { overdueOnly: overdueOnly ? 'true' : undefined },
        }),
        api<Payment[]>('/api/billing/payments'),
        api<(OpenItem & { next_level: number })[]>('/api/billing/dunning'),
      ]);
      setItems(receivables.items);
      setSummary(receivables.summary);
      setCredits(receivables.credits);
      setPayments(paymentList);
      setDunning(dunningList);
    } catch (err) {
      setError(errorText(err));
    }
  }, [overdueOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  function startPayment(item: OpenItem) {
    setPayFor(item);
    setPayment({
      amount: (item.open_cents / 100).toFixed(2).replace('.', ','),
      paidOn: new Date().toISOString().slice(0, 10),
      method: 'ueberweisung',
      reference: item.number,
    });
  }

  async function savePayment() {
    if (!payFor) return;
    const cents = Math.round(Number(payment.amount.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents === 0) return setError('Bitte einen gültigen Betrag angeben.');

    setBusy(true);
    setError('');
    try {
      const result = await api<{ remaining: OpenItem | null }>('/api/billing/payments', {
        method: 'POST',
        body: {
          invoiceId: payFor.id,
          amountCents: cents,
          paidOn: payment.paidOn || null,
          method: payment.method,
          reference: payment.reference || null,
        },
      });
      setMessage(
        result.remaining
          ? `Zahlung gebucht. Offen bleiben ${formatMoney(result.remaining.open_cents)}.`
          : `Rechnung ${payFor.number} ist vollständig ausgeglichen.`,
      );
      setPayFor(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function raiseDunning() {
    setBusy(true);
    try {
      const result = await api<{ raised: number }>('/api/billing/dunning', {
        method: 'POST',
        body: { invoiceIds: dunning.map((item) => item.id) },
      });
      setMessage(`${result.raised} Rechnung(en) auf die nächste Mahnstufe gesetzt.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!items || !summary) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <div className="kpi-tile">
          <div className="label">Offene Posten</div>
          <div className="value">{formatMoney(summary.openCents)}</div>
          <div className="hint">{summary.invoices} Rechnung(en)</div>
        </div>
        <div className={summary.overdueCents > 0 ? 'kpi-tile alert' : 'kpi-tile'}>
          <div className="label">Davon überfällig</div>
          <div className="value">{formatMoney(summary.overdueCents)}</div>
        </div>
        {summary.buckets
          .filter((bucket) => bucket.label !== 'nicht faellig')
          .map((bucket) => (
            <div key={bucket.label} className="kpi-tile">
              <div className="label">{bucket.label.replace('ueber', 'über').replace('faellig', 'fällig')}</div>
              <div className="value">{formatMoney(bucket.cents)}</div>
              <div className="hint">{bucket.count} Rechnung(en)</div>
            </div>
          ))}
      </div>

      {credits.length > 0 && (
        <Card
          title="Guthaben"
          padded={false}
        >
          <div className="body small muted">
            Auf diesen Rechnungen wurde mehr gezahlt als gefordert. Das Guthaben ist mit der
            nächsten Rechnung zu verrechnen oder zurückzuzahlen – eine Rückzahlung wird als
            Zahlung mit negativem Betrag erfasst.
          </div>
          <table>
            <thead>
              <tr>
                <th>Rechnung</th>
                <th>Auftraggeber</th>
                <th className="num">Rechnungsbetrag</th>
                <th className="num">Gezahlt</th>
                <th className="num">Guthaben</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {credits.map((item) => (
                <tr key={item.id}>
                  <td className="mono">{item.number}</td>
                  <td className="small">{item.owner_name}</td>
                  <td className="num">{formatMoney(item.gross_cents)}</td>
                  <td className="num">{formatMoney(item.paid_cents)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {formatMoney(item.paid_cents - item.gross_cents)}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => startPayment(item)}>
                      zurückzahlen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {payFor && (
        <Card title={`Zahlung zu ${payFor.number}`}>
          <div className="small muted" style={{ marginBottom: 10 }}>
            {payFor.owner_name} · offen {formatMoney(payFor.open_cents)} von{' '}
            {formatMoney(payFor.gross_cents)}
          </div>
          <div className="form-grid">
            <TextField
              label="Betrag (€)"
              value={payment.amount}
              onChange={(v) => setPayment({ ...payment, amount: v })}
              hint="negativer Betrag für eine Rückzahlung"
            />
            <TextField
              label="Zahlungsdatum"
              type="date"
              value={payment.paidOn}
              onChange={(v) => setPayment({ ...payment, paidOn: v })}
            />
            <label className="field">
              <span>Zahlungsart</span>
              <select
                value={payment.method}
                onChange={(e) => setPayment({ ...payment, method: e.target.value })}
              >
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {METHOD_LABEL[method]}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Verwendungszweck"
              value={payment.reference}
              onChange={(v) => setPayment({ ...payment, reference: v })}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary" onClick={savePayment} disabled={busy}>
              Zahlung buchen
            </button>
            <button onClick={() => setPayFor(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card
        title="Offene Posten"
        actions={<CheckField label="Nur überfällige" checked={overdueOnly} onChange={setOverdueOnly} />}
        padded={false}
      >
        {items.length === 0 ? (
          <Empty>Keine offenen Posten — alles bezahlt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Rechnung</th>
                <th>Auftraggeber</th>
                <th>Fällig</th>
                <th className="num">Überfällig</th>
                <th className="num">Betrag</th>
                <th className="num">Bezahlt</th>
                <th className="num">Offen</th>
                <th>Mahnstufe</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="mono">{item.number}</td>
                  <td className="small">{item.owner_name}</td>
                  <td className="small">{formatDate(item.due_date)}</td>
                  <td className="num" style={item.days_overdue > 0 ? { color: 'var(--bad)' } : undefined}>
                    {item.days_overdue > 0 ? `${item.days_overdue} Tage` : '—'}
                  </td>
                  <td className="num">{formatMoney(item.gross_cents)}</td>
                  <td className="num muted">{formatMoney(item.paid_cents)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {formatMoney(item.open_cents)}
                  </td>
                  <td>
                    {item.dunning_level > 0 ? (
                      <Badge kind="warn">{item.dunning_level}. Mahnung</Badge>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => startPayment(item)}>
                      Zahlung buchen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid two">
        <Card
          title="Mahnvorschlag"
          actions={
            dunning.length > 0 && (
              <button className="primary small" onClick={raiseDunning} disabled={busy}>
                alle auf nächste Stufe
              </button>
            )
          }
          padded={false}
        >
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Überfällig, noch nicht auf Stufe 3, und die letzte Mahnung liegt mindestens 14 Tage
            zurück.
          </div>
          {dunning.length === 0 ? (
            <Empty>Nichts anzumahnen.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Rechnung</th>
                  <th>Auftraggeber</th>
                  <th className="num">Offen</th>
                  <th className="num">Tage</th>
                  <th>Neue Stufe</th>
                </tr>
              </thead>
              <tbody>
                {dunning.map((item) => (
                  <tr key={item.id}>
                    <td className="mono">{item.number}</td>
                    <td className="small">{item.owner_name}</td>
                    <td className="num">{formatMoney(item.open_cents)}</td>
                    <td className="num" style={{ color: 'var(--bad)' }}>
                      {item.days_overdue}
                    </td>
                    <td>
                      <Badge kind="warn">{item.next_level}. Mahnung</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Letzte Zahlungen" padded={false}>
          {payments.length === 0 ? (
            <Empty>Noch keine Zahlung erfasst.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Rechnung</th>
                    <th>Auftraggeber</th>
                    <th>Art</th>
                    <th className="num">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((entry) => (
                    <tr key={entry.id}>
                      <td className="small">{formatDate(entry.paid_on)}</td>
                      <td className="mono small">{entry.invoice_number}</td>
                      <td className="small">{entry.owner_name}</td>
                      <td className="small">{METHOD_LABEL[entry.method] ?? entry.method}</td>
                      <td className="num">{formatMoney(entry.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
